import React, { useMemo, useState } from 'react';
import { Check, Folder, Plus, Search } from 'lucide-react';
import type { LibraryPlaylist } from './types';

interface LibraryPlaylistPickerProps {
  playlists: LibraryPlaylist[];
  currentPlaylistIds: string[];
  onToggle: (playlistId: string) => void;
  onCreate: (title: string) => void;
  disabled?: boolean;
}

/**
 * Small, searchable multi-membership picker for the current library item.
 */
const LibraryPlaylistPicker: React.FC<LibraryPlaylistPickerProps> = ({
  playlists,
  currentPlaylistIds,
  onToggle,
  onCreate,
  disabled = false
}) => {
  const [query, setQuery] = useState('');
  const [newPlaylistTitle, setNewPlaylistTitle] = useState('');
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const matchingPlaylists = useMemo(
    () => playlists.filter(playlist => playlist.title.toLocaleLowerCase().includes(normalizedQuery)),
    [normalizedQuery, playlists]
  );

  const handleCreate = (event: React.FormEvent) => {
    event.preventDefault();
    const title = newPlaylistTitle.trim();
    if (!title || disabled) return;
    onCreate(title);
    setNewPlaylistTitle('');
  };

  return (
    <div
      role="dialog"
      aria-label="Manage playlists"
      onClick={event => event.stopPropagation()}
      style={{
        position: 'absolute',
        right: '0.75rem',
        bottom: '3.5rem',
        width: 'min(18rem, calc(100% - 1.5rem))',
        padding: '0.75rem',
        backgroundColor: '#111827',
        border: '1px solid #4b5563',
        borderRadius: '0.625rem',
        boxShadow: '0 12px 28px rgba(0, 0, 0, 0.4)',
        zIndex: 5
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '0.5rem',
        marginBottom: '0.625rem'
      }}>
        <strong style={{ fontSize: '0.8125rem', color: '#f9fafb' }}>
          Manage playlists
        </strong>
        <span style={{ fontSize: '0.6875rem', color: '#9ca3af' }}>
          {currentPlaylistIds.length > 0 ? `${currentPlaylistIds.length} selected` : 'None selected'}
        </span>
      </div>

      <div style={{ position: 'relative', marginBottom: '0.625rem' }}>
        <Search
          size={14}
          aria-hidden="true"
          style={{ position: 'absolute', left: '0.625rem', top: '0.55rem', color: '#9ca3af' }}
        />
        <input
          type="search"
          value={query}
          onChange={event => setQuery(event.target.value)}
          placeholder="Search playlists"
          aria-label="Search playlists to add"
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: '0.5rem 0.625rem 0.5rem 1.875rem',
            color: 'white',
            backgroundColor: '#1f2937',
            border: '1px solid #4b5563',
            borderRadius: '0.375rem',
            outline: 'none',
            fontSize: '0.75rem'
          }}
        />
      </div>

      <div style={{ maxHeight: '10rem', overflowY: 'auto', margin: '0 -0.25rem 0.625rem' }}>
        {matchingPlaylists.length > 0 ? matchingPlaylists.map(playlist => {
          const isCurrent = currentPlaylistIds.includes(playlist.id);
          return (
            <button
              key={playlist.id}
              type="button"
              disabled={disabled}
              aria-label={isCurrent
                ? `Remove ${playlist.title} from playlist`
                : `Add ${playlist.title} to playlist`}
              aria-pressed={isCurrent}
              onClick={() => onToggle(playlist.id)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 0.375rem',
                color: isCurrent ? '#dbeafe' : '#d1d5db',
                backgroundColor: isCurrent ? 'rgba(37, 99, 235, 0.2)' : 'transparent',
                border: 'none',
                borderRadius: '0.375rem',
                cursor: disabled ? 'not-allowed' : 'pointer',
                textAlign: 'left',
                opacity: disabled ? 0.6 : 1
              }}
            >
              <span style={{
                width: '1rem',
                height: '1rem',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: `1px solid ${isCurrent ? '#60a5fa' : '#6b7280'}`,
                borderRadius: '0.25rem',
                color: '#bfdbfe',
                flex: '0 0 1rem'
              }}>
                {isCurrent && <Check size={12} strokeWidth={3} />}
              </span>
              <Folder size={14} color={isCurrent ? '#93c5fd' : '#9ca3af'} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {playlist.title}
              </span>
              <span style={{ marginLeft: 'auto', fontSize: '0.625rem', color: '#9ca3af' }}>
                {isCurrent ? 'Remove' : 'Add'}
              </span>
            </button>
          );
        }) : (
          <p style={{ margin: '0.5rem 0.375rem', color: '#9ca3af', fontSize: '0.75rem' }}>
            No playlists found.
          </p>
        )}
      </div>

      <form onSubmit={handleCreate} style={{ display: 'flex', gap: '0.375rem' }}>
        <input
          type="text"
          value={newPlaylistTitle}
          onChange={event => setNewPlaylistTitle(event.target.value)}
          placeholder="New playlist name"
          aria-label="New playlist name"
          style={{
            minWidth: 0,
            flex: 1,
            padding: '0.45rem 0.5rem',
            color: 'white',
            backgroundColor: '#1f2937',
            border: '1px solid #4b5563',
            borderRadius: '0.375rem',
            outline: 'none',
            fontSize: '0.75rem'
          }}
        />
        <button
          type="submit"
          disabled={!newPlaylistTitle.trim() || disabled}
          aria-label="Create and add playlist"
          title="Create and add playlist"
          style={{
            width: '2rem',
            flex: '0 0 2rem',
            padding: 0,
            color: '#dbeafe',
            backgroundColor: '#2563eb',
            border: 'none',
            borderRadius: '0.375rem',
            cursor: !newPlaylistTitle.trim() || disabled ? 'not-allowed' : 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: !newPlaylistTitle.trim() || disabled ? 0.5 : 1
          }}
        >
          <Plus size={15} />
        </button>
      </form>
    </div>
  );
};

export default LibraryPlaylistPicker;
